import { useEffect, useState, useCallback } from "react";
import api, { apiError, fmtUSD } from "@/lib/api";
import { Panel } from "@/components/terminal";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const ELIG_CLS = {
  ELIGIBLE_FOR_OWNER_REVIEW: "text-emerald-400 border-emerald-800",
  NOT_ELIGIBLE: "text-red-400 border-red-900",
  INSUFFICIENT_EVIDENCE: "text-amber-300 border-amber-800",
  BLOCKED: "text-red-400 border-red-900",
};
const RULE_CLS = { PASS: "text-emerald-400 border-emerald-800", FAIL: "text-red-400 border-red-900", BLOCK: "text-red-400 border-red-900" };
const ROB_CLS = { ROBUST: "text-emerald-400 border-emerald-800", MIXED: "text-amber-300 border-amber-800", FRAGILE: "text-red-400 border-red-900", FAILED: "text-red-400 border-red-900", INSUFFICIENT_DATA: "text-slate-400 border-slate-700" };
const VERIF_CLS = { "EVIDENCE-BACKED": "text-emerald-400 border-emerald-800", "UNSUPPORTED": "text-red-400 border-red-900", "NEEDS-TESTING": "text-amber-300 border-amber-800" };
const show = (v, suffix = "") => (v === null || v === undefined ? "Not enough data" : `${v}${suffix}`);
const TIER_CLS = { A: "text-emerald-400 border-emerald-800", B: "text-sky-300 border-sky-800", C: "text-slate-300 border-slate-700", D: "text-amber-300 border-amber-800", E: "text-red-400 border-red-900" };
const QSTATE_CLS = { OBSERVATION: "text-slate-300 border-slate-700", HYPOTHESIS: "text-sky-300 border-sky-800", EXPERIMENT_DESIGNED: "text-sky-300 border-sky-800", RUNNING: "text-amber-300 border-amber-800", RESULT: "text-slate-200 border-slate-600", ROBUSTNESS_REVIEW: "text-amber-300 border-amber-800", CANDIDATE_STRATEGY: "text-emerald-400 border-emerald-800", OWNER_REVIEW: "text-emerald-400 border-emerald-800", REJECTED: "text-red-400 border-red-900" };
const NEXT_STATE = { OBSERVATION: "HYPOTHESIS", HYPOTHESIS: "EXPERIMENT_DESIGNED", EXPERIMENT_DESIGNED: "RUNNING", RUNNING: "RESULT", RESULT: "ROBUSTNESS_REVIEW", ROBUSTNESS_REVIEW: "CANDIDATE_STRATEGY", CANDIDATE_STRATEGY: "OWNER_REVIEW" };

export default function HussarLab() {
  const [datasets, setDatasets] = useState([]);
  const [experiments, setExperiments] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ dataset_id: "FIXTURE_BTC_ACCUMULATION_V1", mode: "WALK_FORWARD",
    initial_capital: 1000, fee_bps: 25, slippage_bps: 5, drawdown_trigger_pct: 3, tranche_usd: 200, cadence_days: 5 });
  const [busy, setBusy] = useState(false);
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [sources, setSources] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [queue, setQueue] = useState([]);
  const [artifacts, setArtifacts] = useState([]);
  const [shadow, setShadow] = useState({ summary: null, cases: [] });

  const loadList = useCallback(() => {
    api.get("/lab/experiments").then((r) => setExperiments(r.data.experiments)).catch(() => {});
  }, []);
  const loadDetail = useCallback((id) => {
    if (!id) return setSelected(null);
    api.get(`/lab/experiments/${id}`).then((r) => setSelected(r.data)).catch(() => setSelected(null));
  }, []);
  const loadResearch = useCallback(() => {
    api.get("/research/sources").then((r) => setSources(r.data.sources)).catch(() => {});
    api.get("/research/snapshots").then((r) => setSnapshots(r.data.snapshots)).catch(() => {});
    api.get("/research/queue").then((r) => setQueue(r.data.items)).catch(() => {});
    api.get("/research/council/artifacts").then((r) => setArtifacts(r.data.artifacts)).catch(() => {});
    api.get("/shadow/summary").then((r) => setShadow((s) => ({ ...s, summary: r.data }))).catch(() => {});
    api.get("/shadow/cases").then((r) => setShadow((s) => ({ ...s, cases: r.data.cases }))).catch(() => {});
  }, []);
  useEffect(() => {
    api.get("/lab/datasets").then((r) => setDatasets(r.data.datasets)).catch(() => {});
    loadList();
    loadResearch();
  }, [loadList, loadResearch]);

  const createAndRun = async () => {
    setBusy(true);
    try {
      const isSnap = form.dataset_id.startsWith("snap:");
      const payload = {
        strategy_id: "ADAPTIVE_DCA", strategy_version: 1, asset_class: "DIGITAL_ASSET",
        mode: form.mode, initial_capital: Number(form.initial_capital),
        fee_bps: Number(form.fee_bps), slippage_bps: Number(form.slippage_bps),
        strategy_parameters: { drawdown_trigger_pct: Number(form.drawdown_trigger_pct),
          tranche_usd: Number(form.tranche_usd), cadence_days: Number(form.cadence_days) },
      };
      if (isSnap) payload.snapshot_id = form.dataset_id.slice(5); else payload.dataset_id = form.dataset_id;
      const c = await api.post("/lab/experiments", payload);
      const run = await api.post(`/lab/experiments/${c.data.id}/run`, {});
      toast.success(run.data.status === "COMPLETED" ? "Experiment completed (hypothetical, simulated)" : `Experiment ${run.data.status}`);
      loadList(); loadDetail(c.data.id);
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const promote = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/lab/experiments/${selected.id}/promote`, { confirm: true, acknowledge_warnings: true });
      toast.success(r.data.idempotent ? "Already promoted (idempotent)" : "Strategy promoted to PAPER — no live authority granted");
      setPromoteOpen(false); loadDetail(selected.id); loadList();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const reject = async () => {
    setBusy(true);
    try {
      await api.post(`/lab/experiments/${selected.id}/reject`, { reason: rejectReason });
      toast.success("Promotion rejected — experiment preserved");
      setRejectOpen(false); setRejectReason(""); loadDetail(selected.id); loadList();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const ingest = async (sid) => {
    setBusy(true);
    try {
      const r = await api.post(`/research/sources/${sid}/ingest`, {});
      toast.success(`Snapshot created: ${r.data.row_count} rows · sha256 ${r.data.checksum.slice(0, 12)}…`);
      loadResearch();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  const generateHypotheses = async () => {
    setBusy(true);
    try {
      const r = await api.post("/research/hypotheses/generate", {});
      toast.success(r.data.created.length ? `${r.data.created.length} GENERATED hypotheses added` : `No new hypotheses (${r.data.skipped.length} existing)`);
      loadResearch();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  const queueTransition = async (q, toState) => {
    setBusy(true);
    try {
      await api.post(`/research/queue/${q.id}/transition`, { to_state: toState });
      loadResearch();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const requestCritique = async () => {
    setBusy(true);
    try {
      const r = await api.post("/research/council/critique", { experiment_id: selected.id });
      const s = r.data.summary;
      toast.success(`AI critique (AI-R): ${s.methodology_concerns} concerns · ${s.evidence_backed} evidence-backed · ${s.unsupported} unsupported`);
      loadDetail(selected.id); loadResearch();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const evaluateShadow = async () => {
    setBusy(true);
    try {
      const r = await api.post("/shadow/evaluate", {});
      toast.success(`Shadow evaluation: ${r.data.filled} outcome(s) filled, ${r.data.refused} refused`);
      loadResearch();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const m = selected?.results?.metrics;
  const elig = selected?.eligibility;
  const rob = selected?.results?.robustness;
  const promoted = (selected?.promotions || []).find((p) => p.status === "PROMOTED");
  const artifact = artifacts.filter((a) => a.experiment_id === selected?.id)[0];
  const rejected = (selected?.promotions || []).find((p) => p.status === "REJECTED");

  return (
    <div className="space-y-5" data-testid="hussar-lab-page">
      <div>
        <h2 className="font-display text-xl font-bold" data-testid="lab-title">Hussar Lab</h2>
        <p className="text-xs text-slate-500 font-mono mt-1 max-w-3xl">
          Research environment: strategies are tested against deterministic simulated datasets before they may be
          considered for PAPER evaluation. LAB has no access to credentials, private APIs, orders, withdrawals or
          transfers. A passing experiment means eligible for owner review — never approved for trading.
        </p>
      </div>
      <div className="panel px-4 py-2.5 border-amber-700 flex flex-wrap items-center gap-x-4 gap-y-1" data-testid="lab-hypothetical-banner">
        <span className="text-[10px] font-mono font-bold tracking-widest text-amber-300">HYPOTHETICAL RESULTS</span>
        <span className="text-[10px] font-mono text-slate-500">All Lab results are simulated and do not predict future returns. Active asset class: <span className="text-slate-300" data-testid="lab-asset-class">DIGITAL_ASSET</span></span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel title="Experiments" testId="lab-experiments-panel">
          <div className="overflow-x-auto">
            <table className="w-full text-xs font-mono" data-testid="lab-experiments-table">
              <thead><tr className="text-left text-slate-500 border-b border-slate-800">
                <th className="py-1.5 pr-2">Strategy</th><th className="pr-2">Dataset</th><th className="pr-2">Status</th><th className="pr-2">Eligibility</th><th className="pr-2">Promotion</th>
              </tr></thead>
              <tbody>
                {experiments.map((e) => (
                  <tr key={e.id} className="border-b border-slate-800/50 cursor-pointer hover:bg-slate-800/20"
                    data-testid={`lab-experiment-row-${e.id}`} onClick={() => loadDetail(e.id)}>
                    <td className="py-1.5 pr-2 text-slate-200">{e.strategy_id} v{e.strategy_version}</td>
                    <td className="pr-2 text-slate-400">{e.dataset_id}</td>
                    <td className="pr-2 text-slate-300" data-testid={`lab-exp-status-${e.id}`}>{e.status}</td>
                    <td className="pr-2">{e.eligibility
                      ? <span className={`text-[9px] px-1.5 py-0.5 border rounded-sm ${ELIG_CLS[e.eligibility.status]}`}>{e.eligibility.status}</span>
                      : <span className="text-slate-600">—</span>}</td>
                    <td className="pr-2 text-slate-400">{e.promotion_status || "—"}</td>
                  </tr>))}
                {experiments.length === 0 && (
                  <tr><td colSpan="5" className="py-3 text-slate-500" data-testid="lab-experiments-empty">No experiments yet for this account.</td></tr>)}
              </tbody>
            </table>
          </div>
        </Panel>

        <Panel title="New Experiment" testId="new-experiment-panel">
          <div className="space-y-3 text-xs font-mono" data-testid="new-experiment-form">
            <div className="grid grid-cols-2 gap-3">
              <div><div className="metric-label">Strategy</div><div className="text-slate-200 mt-0.5">Adaptive DCA v1</div></div>
              <div><div className="metric-label">Mode</div>
                <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} data-testid="mode-select"
                  className="bg-[#111318] border border-slate-700 text-slate-100 px-2 py-1 rounded-sm w-full outline-none">
                  <option value="WALK_FORWARD">Walk-Forward</option>
                  <option value="HISTORICAL">Historical Backtest</option>
                  <option value="SCENARIO_SUITE">Scenario Suite (simulated)</option>
                </select></div>
              <div><div className="metric-label">{form.dataset_id.startsWith("snap:") ? "Dataset (immutable snapshot — real external evidence)" : "Dataset (simulated fixture)"}</div>
                <select value={form.dataset_id} onChange={(e) => setForm({ ...form, dataset_id: e.target.value })} data-testid="dataset-select"
                  className="bg-[#111318] border border-slate-700 text-slate-100 px-2 py-1 rounded-sm w-full outline-none">
                  {datasets.map((d) => <option key={d.dataset_id} value={d.dataset_id}>{d.dataset_id}</option>)}
                  {snapshots.map((s) => <option key={s.id} value={`snap:${s.id}`}>SNAPSHOT · {s.source_id} v{s.snapshot_version} · {s.start_at}→{s.end_at}</option>)}
                </select></div>
              <div><div className="metric-label">Starting capital $</div>
                <input type="number" value={form.initial_capital} onChange={(e) => setForm({ ...form, initial_capital: e.target.value })}
                  data-testid="capital-input" className="bg-[#111318] border border-slate-700 text-slate-100 px-2 py-1 rounded-sm w-full outline-none" /></div>
              <div><div className="metric-label">Fee bps</div>
                <input type="number" value={form.fee_bps} onChange={(e) => setForm({ ...form, fee_bps: e.target.value })}
                  data-testid="fee-input" className="bg-[#111318] border border-slate-700 text-slate-100 px-2 py-1 rounded-sm w-full outline-none" /></div>
              <div><div className="metric-label">Slippage bps</div>
                <input type="number" value={form.slippage_bps} onChange={(e) => setForm({ ...form, slippage_bps: e.target.value })}
                  data-testid="slippage-input" className="bg-[#111318] border border-slate-700 text-slate-100 px-2 py-1 rounded-sm w-full outline-none" /></div>
              <div><div className="metric-label">Drawdown trigger %</div>
                <input type="number" value={form.drawdown_trigger_pct} onChange={(e) => setForm({ ...form, drawdown_trigger_pct: e.target.value })}
                  data-testid="trigger-input" className="bg-[#111318] border border-slate-700 text-slate-100 px-2 py-1 rounded-sm w-full outline-none" /></div>
              <div><div className="metric-label">Tranche $</div>
                <input type="number" value={form.tranche_usd} onChange={(e) => setForm({ ...form, tranche_usd: e.target.value })}
                  data-testid="tranche-input" className="bg-[#111318] border border-slate-700 text-slate-100 px-2 py-1 rounded-sm w-full outline-none" /></div>
            </div>
            <p className="text-[10px] text-slate-600">
              Benchmark: hypothetical buy &amp; hold of the tested instrument (experiment-specific — never the
              real-account benchmark). Configuration is recorded immutably before execution.
            </p>
            <Button data-testid="create-experiment-button" onClick={createAndRun} disabled={busy}
              className="bg-[#D4AF37] hover:bg-[#C5A059] text-[#0B0C0E] font-bold">
              {busy ? "RUNNING…" : "CREATE & RUN EXPERIMENT"}</Button>
          </div>
        </Panel>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="research-fabric-section">
        <Panel title="Research Sources" testId="research-sources-panel">
          <div className="space-y-1.5 text-xs font-mono">
            {sources.map((s) => (
              <div key={s.source_id} className="flex items-center gap-2 border-b border-slate-800/50 py-1.5"
                data-testid={`source-row-${s.source_id}`}>
                <span className={`text-[9px] px-1.5 py-0.5 border rounded-sm shrink-0 ${TIER_CLS[s.quality_tier]}`}
                  data-testid={`source-tier-${s.source_id}`}>{s.quality_tier}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-slate-200 truncate">{s.source_id}</div>
                  <div className="text-[10px] text-slate-500 truncate">{s.provider} · {s.evidence_tier} · {s.frequency} · <span className={s.freshness === "STALE" ? "text-amber-300" : "text-slate-500"} data-testid={`source-freshness-${s.source_id}`}>{s.freshness || "NEVER_INGESTED"}</span></div>
                </div>
                <span className="text-[9px] text-slate-500 shrink-0" data-testid={`source-status-${s.source_id}`}>{s.status}</span>
                <Button size="sm" variant="outline" data-testid={`ingest-button-${s.source_id}`}
                  disabled={busy || s.status !== "ACTIVE"} onClick={() => ingest(s.source_id)}
                  className="border-slate-700 text-slate-300 text-[10px] h-6 px-2">INGEST</Button>
              </div>))}
            {sources.length === 0 && <p className="text-slate-500 py-2" data-testid="sources-empty">No sources registered.</p>}
          </div>
        </Panel>
        <Panel title="Dataset Snapshots" testId="research-snapshots-panel">
          <div className="space-y-1.5 text-xs font-mono">
            {snapshots.map((s) => (
              <div key={s.id} className="border-b border-slate-800/50 py-1.5" data-testid={`snapshot-row-${s.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-slate-200 truncate">{s.source_id} v{s.snapshot_version}</span>
                  <Button size="sm" variant="outline" data-testid={`use-snapshot-${s.id}`}
                    onClick={() => setForm({ ...form, dataset_id: `snap:${s.id}` })}
                    className="border-slate-700 text-slate-300 text-[10px] h-6 px-2">USE IN EXPERIMENT</Button>
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  {s.row_count} rows · {s.start_at} → {s.end_at} · sha256 {s.checksum?.slice(0, 12)}… · immutable
                </div>
              </div>))}
            {snapshots.length === 0 && <p className="text-slate-500 py-2" data-testid="snapshots-empty">No snapshots yet — ingest a source.</p>}
          </div>
        </Panel>
        <Panel title="Research Queue" testId="research-queue-panel"
          right={<Button size="sm" variant="outline" data-testid="generate-hypotheses-button" disabled={busy}
            onClick={generateHypotheses} className="border-amber-800 text-amber-300 text-[10px] h-6 px-2">GENERATE HYPOTHESES</Button>}>
          <div className="space-y-1.5 text-xs font-mono">
            {queue.map((q) => (
              <div key={q.id} className="border-b border-slate-800/50 py-1.5" data-testid={`queue-item-${q.id}`}>
                <div className="flex items-center gap-2">
                  <span className={`text-[9px] px-1.5 py-0.5 border rounded-sm shrink-0 ${QSTATE_CLS[q.state] || "text-slate-400 border-slate-700"}`}
                    data-testid={`queue-state-${q.id}`}>{q.state}</span>
                  {q.origin === "GENERATED" && (
                    <span className="text-[9px] px-1.5 py-0.5 border rounded-sm text-sky-300 border-sky-800 shrink-0"
                      data-testid={`queue-generated-badge-${q.id}`}>GENERATED</span>)}
                </div>
                <div className="text-slate-300 mt-1 leading-snug">{q.title}</div>
                <div className="text-[10px] text-slate-600 mt-0.5">{q.citations?.length || 0} citations · {q.transition_count ?? 0} transitions</div>
                {q.state !== "REJECTED" && q.state !== "OWNER_REVIEW" && (
                  <div className="flex gap-2 mt-1.5">
                    <Button size="sm" variant="outline" data-testid={`queue-advance-${q.id}`} disabled={busy}
                      onClick={() => queueTransition(q, NEXT_STATE[q.state])}
                      className="border-slate-700 text-slate-300 text-[10px] h-6 px-2">
                      → {NEXT_STATE[q.state]}</Button>
                    <Button size="sm" variant="outline" data-testid={`queue-reject-${q.id}`} disabled={busy}
                      onClick={() => queueTransition(q, "REJECTED")}
                      className="border-red-900 text-red-400 text-[10px] h-6 px-2">REJECT</Button>
                  </div>)}
              </div>))}
            {queue.length === 0 && <p className="text-slate-500 py-2" data-testid="queue-empty">No research items yet.</p>}
          </div>
        </Panel>
      </div>

      <Panel title="Hussar Shadow — Counterfactual Record" testId="shadow-panel"
        right={<Button size="sm" variant="outline" data-testid="shadow-evaluate-button" disabled={busy}
          onClick={evaluateShadow} className="border-slate-700 text-slate-300 text-[10px] h-6 px-2">EVALUATE DUE</Button>}>
        <p className="text-[10px] font-mono text-amber-300/90 border border-amber-900/60 rounded-sm px-2 py-1 mb-3"
          data-testid="shadow-modeled-label">MODELED COUNTERFACTUAL — NOT REALIZED RETURN · evidence class HUSSAR_SHADOW, never blended with EXTERNAL / HUSSAR_PAPER / HUSSAR_LIVE</p>
        {shadow.summary && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs font-mono mb-3" data-testid="shadow-summary">
            <div><div className="metric-label">Cases</div><div className="text-slate-100 mt-0.5" data-testid="shadow-total-cases">{shadow.summary.total_cases} ({shadow.summary.blocked_cases} blocked)</div></div>
            <div><div className="metric-label">Outcomes evaluated</div><div className="text-slate-100 mt-0.5">{shadow.summary.evaluated_outcomes}</div></div>
            <div><div className="metric-label">Decision regret (modeled)</div><div className="text-slate-100 mt-0.5" data-testid="shadow-total-regret">${shadow.summary.total_decision_regret_usd}</div></div>
            <div><div className="metric-label">Capital protected (modeled)</div><div className="text-slate-100 mt-0.5" data-testid="shadow-total-protected">${shadow.summary.total_capital_protected_usd}</div></div>
            <div><div className="metric-label">Opportunity foregone (modeled)</div><div className="text-slate-100 mt-0.5" data-testid="shadow-total-foregone">${shadow.summary.total_opportunity_foregone_usd}</div></div>
          </div>)}
        <div className="space-y-1.5 text-xs font-mono">
          {shadow.cases.slice(0, 6).map((c) => (
            <div key={c.id} className="border-b border-slate-800/50 py-1.5" data-testid={`shadow-case-${c.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-slate-200">{c.decision_type} {c.asset} ${c.chosen_action.amount_usd}</span>
                <span className="text-[9px] text-slate-500">{c.frozen_context.market_regime} · policy v{c.policy_version} · {c.execution_mode || "PAPER"}</span>
                <span className="text-[9px] text-slate-600">{String(c.decision_timestamp).slice(0, 10)}</span>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap mt-1">
                {c.alternatives.map((a) => (
                  <span key={a.kind} className={`text-[9px] px-1 py-0.5 border rounded-sm ${a.admissibility === "ADMISSIBLE" ? "text-slate-300 border-slate-700" : "text-red-400 border-red-900"}`}
                    data-testid={`shadow-alt-${c.id}-${a.kind}`}>{a.kind}{a.amount_usd ? ` $${a.amount_usd}` : ""} · {a.admissibility}</span>))}
              </div>
              <div className="flex items-center gap-3 mt-1 text-[10px] text-slate-500">
                {["1D", "7D", "30D"].map((h) => (
                  <span key={h} data-testid={`shadow-outcome-${c.id}-${h}`}>
                    {h}: {c.outcomes?.[h] ? `chosen $${c.outcomes[h].chosen.pnl_usd} · regret $${c.decision_regret?.[h]?.regret_usd}` : "pending"}
                  </span>))}
                {Object.values(c.capital_protected || {}).some((v) => v > 0) && (
                  <span className="text-sky-300" data-testid={`shadow-protected-${c.id}`}>protected ${Object.values(c.capital_protected).reduce((a, b) => a + b, 0).toFixed(2)}</span>)}
                {Object.values(c.opportunity_foregone || {}).some((v) => v > 0) && (
                  <span className="text-amber-300" data-testid={`shadow-foregone-${c.id}`}>foregone ${Object.values(c.opportunity_foregone).reduce((a, b) => a + b, 0).toFixed(2)}</span>)}
              </div>
            </div>))}
          {shadow.cases.length === 0 && <p className="text-slate-500 py-2" data-testid="shadow-empty">No shadow cases yet — they are recorded automatically at every eligible decision.</p>}
        </div>
      </Panel>

      {selected && (
        <div className="space-y-4" data-testid="experiment-detail">
          <Panel title={`Experiment ${selected.id.slice(0, 8)} — ${selected.strategy_id} v${selected.strategy_version}`}
            testId="experiment-metadata-panel"
            right={<span className="text-[10px] font-mono text-slate-500">{selected.status}</span>}>
            {selected.explanation && (
              <p className="text-xs text-slate-300 font-mono leading-relaxed mb-3" data-testid="explanation-text">{selected.explanation}</p>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs font-mono">
              <div><div className="metric-label">Dataset</div><div className="text-slate-200 mt-0.5" data-testid="meta-dataset">{selected.dataset_id}</div></div>
              <div><div className="metric-label">Dataset hash</div><div className="text-slate-400 mt-0.5 truncate" data-testid="meta-dataset-hash">{selected.dataset_hash?.slice(0, 16)}…</div></div>
              <div><div className="metric-label">Period</div><div className="text-slate-200 mt-0.5">{selected.start_at} → {selected.end_at}</div></div>
              <div><div className="metric-label">Engine</div><div className="text-slate-400 mt-0.5">{selected.engine_version} · seed {selected.random_seed ?? "none"}</div></div>
              <div><div className="metric-label">Evidence (never blended)</div>
                <div className="text-slate-400 mt-0.5" data-testid="evidence-tiers">
                  EXTERNAL {selected.evidence?.EXTERNAL?.length ?? 0} · HUSSAR_PAPER {selected.evidence?.HUSSAR_PAPER?.length ?? 0} · HUSSAR_LIVE {selected.evidence?.HUSSAR_LIVE?.length ?? 0}
                </div></div>
            </div>
          </Panel>

          {m && (
            <>
              <Panel title="Performance Metrics (hypothetical)" testId="metrics-panel">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs font-mono" data-testid="metrics-grid">
                  <div><div className="metric-label">Total return</div><div className="text-slate-100 mt-0.5" data-testid="metric-total-return">{show(m.total_return_pct, "%")}</div></div>
                  <div><div className="metric-label">Benchmark return</div><div className="text-slate-100 mt-0.5" data-testid="metric-benchmark-return">{show(m.benchmark_return_pct, "%")}</div></div>
                  <div><div className="metric-label">Excess return</div><div className="text-slate-100 mt-0.5">{show(m.excess_return_pct, "%")}</div></div>
                  <div><div className="metric-label">Max drawdown</div><div className="text-slate-100 mt-0.5" data-testid="metric-max-dd">{show(m.max_drawdown_pct, "%")}</div></div>
                  <div><div className="metric-label">Sharpe</div><div className="text-slate-100 mt-0.5" data-testid="metric-sharpe">{show(m.sharpe)}</div></div>
                  <div><div className="metric-label">Sortino</div><div className="text-slate-100 mt-0.5">{show(m.sortino)}</div></div>
                  <div><div className="metric-label">Trades</div><div className="text-slate-100 mt-0.5">{m.trade_count}</div></div>
                  <div><div className="metric-label">Win rate</div><div className="text-slate-100 mt-0.5" data-testid="metric-win-rate">{show(m.win_rate_pct, "%")}</div></div>
                  <div><div className="metric-label">Gross fees</div><div className="text-slate-100 mt-0.5">{show(m.gross_fees_usd === null ? null : fmtUSD(m.gross_fees_usd))}</div></div>
                  <div><div className="metric-label">Slippage cost</div><div className="text-slate-100 mt-0.5">{show(m.slippage_cost_usd === null ? null : fmtUSD(m.slippage_cost_usd))}</div></div>
                  <div><div className="metric-label">Turnover</div><div className="text-slate-100 mt-0.5">{show(m.turnover)}</div></div>
                  <div><div className="metric-label">Risk interventions</div><div className="text-slate-100 mt-0.5" data-testid="metric-rg-interventions">{m.risk_governor_interventions}</div></div>
                </div>
                <p className="text-[10px] text-slate-600 font-mono mt-3">{m.benchmark_note} Sharpe: {m.sharpe_note}. Sortino: {m.sortino_note}.</p>
              </Panel>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Panel title="Equity Curve" testId="equity-curve-panel">
                  <div className="h-48" data-testid="equity-curve-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={selected.results.equity_curve} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
                        <XAxis dataKey="date" hide /><YAxis domain={["auto", "auto"]} width={50} tick={{ fontSize: 9 }} />
                        <Tooltip /><Line type="monotone" dataKey="equity" stroke="#D4AF37" dot={false} strokeWidth={1.5} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </Panel>
                <Panel title="Drawdown" testId="drawdown-panel">
                  <div className="h-48" data-testid="drawdown-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={selected.results.drawdown_curve} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
                        <XAxis dataKey="date" hide /><YAxis width={50} tick={{ fontSize: 9 }} />
                        <Tooltip /><Line type="monotone" dataKey="drawdown_pct" stroke="#B91C1C" dot={false} strokeWidth={1.5} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </Panel>
              </div>

              <Panel title="Robustness" testId="robustness-panel"
                right={rob && <span className={`text-[10px] font-mono px-2 py-0.5 border rounded-sm ${ROB_CLS[rob.status]}`} data-testid="robustness-status">{rob.status}</span>}>
                <div className="space-y-1.5 text-xs font-mono">
                  {(rob?.checks || []).map((c) => (
                    <div key={c.variant} className="flex items-start gap-3" data-testid={`robustness-check-${c.variant}`}>
                      <span className={`text-[9px] px-1.5 py-0.5 border rounded-sm ${c.passed ? "text-emerald-400 border-emerald-800" : "text-red-400 border-red-900"}`}>{c.passed ? "PASS" : "FAIL"}</span>
                      <span className="text-slate-300 w-44 shrink-0">{c.variant.replace(/_/g, " ")}</span>
                      <span className="text-slate-500">{c.reason}</span>
                    </div>))}
                </div>
                {selected.results.walk_forward && (
                  <div className="text-xs font-mono text-slate-400 mt-3" data-testid="walkforward-summary">
                    Walk-forward: in-sample {show(selected.results.walk_forward.train_metrics.total_return_pct, "%")} →
                    out-of-sample {show(selected.results.walk_forward.oos_metrics.total_return_pct, "%")}
                    {" "}(isolated windows, fixed parameters).
                  </div>)}
              </Panel>

              {selected.results.fills?.length > 0 && (
                <Panel title="Simulated Trades" testId="trades-panel">
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs font-mono" data-testid="trades-table">
                      <thead><tr className="text-left text-slate-500 border-b border-slate-800">
                        <th className="py-1.5 pr-2">Date</th><th className="pr-2">Side</th><th className="pr-2">Amount</th><th className="pr-2">Price</th><th className="pr-2">Fee</th>
                      </tr></thead>
                      <tbody>
                        {selected.results.fills.map((f, i) => (
                          <tr key={i} className="border-b border-slate-800/50">
                            <td className="py-1.5 pr-2 text-slate-300">{f.date}</td><td className="pr-2 text-slate-300">{f.side}</td>
                            <td className="pr-2 text-slate-200">{fmtUSD(f.amount_usd)}</td><td className="pr-2 text-slate-300">{f.price}</td>
                            <td className="pr-2 text-slate-400">{fmtUSD(f.fee_usd)}</td>
                          </tr>))}
                      </tbody>
                    </table>
                  </div>
                </Panel>)}

              {elig && (
                <Panel title="Eligibility Checklist" testId="eligibility-panel"
                  right={<span className={`text-[10px] font-mono px-2 py-0.5 border rounded-sm ${ELIG_CLS[elig.status]}`} data-testid="eligibility-status">{elig.status}</span>}>
                  <div className="space-y-1.5" data-testid="eligibility-checklist">
                    {elig.rules.map((r) => (
                      <div key={r.rule} className="flex items-start gap-3 text-xs font-mono" data-testid={`eligibility-rule-${r.rule}`}>
                        <span className={`text-[9px] px-1.5 py-0.5 border rounded-sm shrink-0 ${RULE_CLS[r.status]}`}>{r.status}</span>
                        <span className="text-slate-300 w-56 shrink-0">{r.rule.replace(/_/g, " ")}</span>
                        <span className="text-slate-500 w-44 shrink-0 hidden md:inline">{String(r.threshold)} · observed {String(r.observed)}</span>
                        <span className="text-slate-600">{r.explanation}</span>
                      </div>))}
                  </div>
                  {!promoted && !rejected && selected.status === "COMPLETED" && (
                    <div className="flex gap-2 mt-4">
                      <Button data-testid="promote-button" onClick={() => setPromoteOpen(true)} size="sm"
                        disabled={elig.status !== "ELIGIBLE_FOR_OWNER_REVIEW"}
                        className="bg-emerald-700 hover:bg-emerald-600 text-white font-bold">PROMOTE TO PAPER</Button>
                      <Button data-testid="reject-button" onClick={() => setRejectOpen(true)} size="sm" variant="outline"
                        className="border-red-900 text-red-400 hover:bg-red-950/30">REJECT</Button>
                    </div>)}
                  {promoted && <p className="text-[10px] font-mono text-emerald-400 mt-3" data-testid="promoted-note">PROMOTED TO PAPER by {promoted.promoted_by} — paper evaluation only, no live authority.</p>}
                  {rejected && <p className="text-[10px] font-mono text-red-400 mt-3" data-testid="rejected-note">PROMOTION REJECTED by {rejected.rejected_by}{rejected.reason ? ` — ${rejected.reason}` : ""}. Experiment preserved.</p>}
                </Panel>)}
            </>
          )}
          {selected.status === "COMPLETED" && (
            <Panel title="AI Research Council — Critique (AI-R, non-authoritative)" testId="ai-critique-panel"
              right={<Button size="sm" variant="outline" data-testid="ai-critique-button" disabled={busy}
                onClick={requestCritique} className="border-sky-800 text-sky-300 text-[10px] h-6 px-2">
                {artifact ? "NEW CRITIQUE" : "REQUEST AI CRITIQUE"}</Button>}>
              {!artifact && <p className="text-[10px] font-mono text-slate-500" data-testid="ai-critique-empty">
                No AI critique yet. AI output is AI_DERIVED_RESEARCH: it may critique and suggest, but no claim
                enters the Research Queue until verified against the Research Data Fabric. Strategy status and
                production policy are never affected.</p>}
              {artifact && (
                <div className="space-y-3 text-xs font-mono">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ai-critique-summary">
                    <div><div className="metric-label">Methodology concerns</div><div className="text-slate-100 mt-0.5" data-testid="ai-summary-concerns">{artifact.summary.methodology_concerns}</div></div>
                    <div><div className="metric-label">Evidence-backed</div><div className="text-emerald-400 mt-0.5" data-testid="ai-summary-verified">{artifact.summary.evidence_backed}</div></div>
                    <div><div className="metric-label">Unsupported</div><div className="text-red-400 mt-0.5" data-testid="ai-summary-unsupported">{artifact.summary.unsupported}</div></div>
                    <div><div className="metric-label">Needs testing</div><div className="text-amber-300 mt-0.5" data-testid="ai-summary-needs-testing">{artifact.summary.needs_testing}</div></div>
                    <div><div className="metric-label">Suggested experiments</div><div className="text-slate-100 mt-0.5" data-testid="ai-summary-suggested">{artifact.summary.suggested_experiments}</div></div>
                  </div>
                  <p className="text-[10px] text-slate-500" data-testid="ai-impact-line">
                    Strategy status: <span className="text-slate-300">unchanged</span> · Production impact: <span className="text-slate-300">none</span> ·{" "}
                    {artifact.provider_id} · {artifact.latency_ms} ms · prompt sha256 {artifact.prompt_hash.slice(0, 12)}… · response sha256 {artifact.response_hash.slice(0, 12)}…
                  </p>
                  <div className="space-y-1.5">
                    {artifact.claims.map((c, i) => (
                      <div key={i} className="flex items-start gap-3" data-testid={`ai-claim-${i}`}>
                        <span className={`text-[9px] px-1.5 py-0.5 border rounded-sm shrink-0 ${VERIF_CLS[c.verification.status]}`} data-testid={`ai-verification-${i}`}>{c.verification.status}</span>
                        <span className="text-slate-300">{c.claim}</span>
                        <span className="text-slate-600 hidden md:inline">{c.verification.detail}</span>
                      </div>))}
                  </div>
                  {artifact.suggested_experiments?.length > 0 && (
                    <div className="text-[10px] text-slate-500" data-testid="ai-suggested-list">
                      Suggested follow-ups (recorded, NOT queued): {artifact.suggested_experiments.map((s) => s.title).join(" · ")}
                    </div>)}
                </div>)}
            </Panel>)}
          {selected.status === "FAILED" && (
            <Panel title="Experiment Failed" testId="experiment-failed-panel">
              <p className="text-xs font-mono text-red-400" data-testid="failure-reason">{selected.failure_reason}</p>
            </Panel>)}
        </div>
      )}

      <AlertDialog open={promoteOpen} onOpenChange={setPromoteOpen}>
        <AlertDialogContent data-testid="promote-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Promote {selected?.strategy_id} v{selected?.strategy_version} to PAPER?</AlertDialogTitle>
            <AlertDialogDescription>
              Experiment {selected?.id} · {selected?.dataset_id} · {selected?.start_at} → {selected?.end_at} ·
              hypothetical return {m?.total_return_pct}% vs benchmark {m?.benchmark_return_pct}% · max drawdown{" "}
              {m?.max_drawdown_pct}% · robustness {rob?.status} · eligibility {elig?.status}.
              Results are hypothetical and simulated — they do not predict future returns.
              PAPER promotion grants NO live-execution authority.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="promote-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="promote-confirm-button" onClick={promote} disabled={busy}>
              {busy ? "PROMOTING…" : "CONFIRM — PROMOTE TO PAPER"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <AlertDialogContent data-testid="reject-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Reject promotion for this experiment?</AlertDialogTitle>
            <AlertDialogDescription>
              The experiment is preserved permanently. Optionally record a reason.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} data-testid="reject-reason-input"
            placeholder="Reason (optional)"
            className="bg-[#111318] border border-slate-700 text-slate-100 px-3 py-2 rounded-sm font-mono text-xs w-full outline-none" />
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reject-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="reject-confirm-button" onClick={reject} disabled={busy}>
              {busy ? "REJECTING…" : "CONFIRM — REJECT"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
